import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, movies as allMovies, categories } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = useMemo(() => getMovieById(Number(id)), [id]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return allMovies
      .filter((m) => m.id !== movie.id && m.category === movie.category)
      .slice(0, 5);
  }, [movie]);

  if (!movie) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <p className="text-foreground-300 text-lg">未找到该影视内容</p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <BackToTop />
      </>
    );
  }

  const categoryName = categories.find((c) => c.slug === movie.category)?.name || movie.category;

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="BreadcrumbList" />
      <SchemaOrg
        type="Article"
        data={{
          headline: movie.title,
          description: movie.description,
          datePublished: movie.releaseDate,
        }}
      />

      <main className="pt-20 md:pt-24">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-6 py-4">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-sm"></i>
              <Link to={`/category/${movie.category}`} className="hover:text-primary-500 transition-colors duration-200">
                {categoryName}
              </Link>
              <i className="ri-arrow-right-s-line text-sm"></i>
              <span className="text-foreground-200">{movie.title}</span>
            </nav>
          </div>
        </div>

        {/* Banner */}
        <section className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="relative w-full h-[200px] md:h-[350px] rounded-xl overflow-hidden">
              <img
                src={movie.banner}
                alt={`${movie.title} - 88影视网高清在线观看`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/20 to-transparent"></div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Poster */}
              <div className="w-full lg:w-[280px] shrink-0">
                <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} 海报 - 高清电影电视剧`}
                    title={`${movie.title} - 88影视网`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between p-3 bg-background-100 rounded-lg">
                    <span className="text-xs text-foreground-500">评分</span>
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-primary-500"></i>
                      <span className="text-lg font-bold text-foreground-50">{movie.rating}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-background-100 rounded-lg">
                    <span className="text-xs text-foreground-500">画质</span>
                    <span className="text-sm font-semibold text-primary-500">{movie.quality}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-background-100 rounded-lg">
                    <span className="text-xs text-foreground-500">年份</span>
                    <span className="text-sm text-foreground-200">{movie.year}</span>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-1">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-6">{movie.titleEn}</p>

                <div className="flex flex-wrap items-center gap-2 mb-6">
                  {movie.genre.map((g) => (
                    <span key={g} className="px-2.5 py-1 text-xs bg-accent-500/10 text-accent-500 rounded-md">
                      {g}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-300 leading-relaxed mb-8">
                  {movie.longDescription}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 p-3 bg-background-100 rounded-lg">
                    <span className="text-xs text-foreground-500 shrink-0 w-14">导演</span>
                    <span className="text-sm text-foreground-200">{movie.director}</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-background-100 rounded-lg">
                    <span className="text-xs text-foreground-500 shrink-0 w-14">地区</span>
                    <span className="text-sm text-foreground-200">{movie.region}</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-background-100 rounded-lg">
                    <span className="text-xs text-foreground-500 shrink-0 w-14">时长</span>
                    <span className="text-sm text-foreground-200">{movie.duration}</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-background-100 rounded-lg">
                    <span className="text-xs text-foreground-500 shrink-0 w-14">上映</span>
                    <span className="text-sm text-foreground-200">{movie.releaseDate}</span>
                  </div>
                </div>

                <div className="p-4 bg-background-100 rounded-lg mb-8">
                  <h4 className="text-sm font-semibold text-foreground-100 mb-3">主演</h4>
                  <div className="flex flex-wrap gap-2">
                    {movie.cast.map((actor) => (
                      <span key={actor} className="px-3 py-1.5 text-xs bg-background-200 text-foreground-300 rounded-lg">
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-play-circle-fill text-lg"></i>
                  立即观看
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Related Movies */}
        {relatedMovies.length > 0 && (
          <section className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-50 mb-6">
                同类推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}